// Tableau de bord Geotab : les mesures du bandeau pour une portée (toute la période ou la dernière semaine) et pour chaque journée.
// Les journées viennent du serveur (bar.days) ; une semaine est la somme de ses journées, les durées pondérées par étape mesurée.
import { fmtDelta, fmtDuration } from "./geotabFormat.js";
import { resolveMetricDelta } from "../../utils/dashboardComparison.js";

const pct = (done, total) => (total ? (100 * done) / total : null);
const percent = (v) => `${Math.round(v)} %`;
const STAGES = ["route", "loading", "delivery"];
const stageGap = (durations, key) => (durations?.[key]?.count ? Math.round(durations[key].real) - Math.round(durations[key].planned) : null);

export function addDays(iso, days) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const addNumbers = (a, b) => (typeof b === "number" ? (a ?? 0) + b : Object.fromEntries(Object.keys(b).map((k) => [k, addNumbers(a?.[k], b[k])])));

function sumDurations(list) {
  return Object.fromEntries(STAGES.map((key) => {
    const items = list.map((d) => d?.[key]).filter((d) => d?.count);
    const count = items.reduce((n, d) => n + d.count, 0);
    const of = list.reduce((n, d) => n + (d?.[key]?.of || 0), 0);
    if (!count) return [key, { count: 0, of }];
    const mean = (field) => items.reduce((n, d) => n + d[field] * d.count, 0) / count;
    const near = items.reduce((n, d) => n + d.near, 0);
    return [key, { count, of, planned: mean("planned"), real: mean("real"), spread: mean("spread"), near, near_pct: Math.round((100 * near) / count) }];
  }));
}

// Une portée : ses journées, leur barre additionnée et leurs durées.
const countsOf = ({ date, durations, ...counts }) => counts; // eslint-disable-line no-unused-vars

export function scopeOf(days) {
  return { days, bar: days.reduce((total, day) => addNumbers(total, countsOf(day)), {}), durations: sumDurations(days.map((d) => d.durations)) };
}

// Toute la période (totaux exacts du serveur), sa dernière semaine (dimanche-samedi) et la semaine d'avant, pour l'écart.
export function dashboardScopes(bar) {
  const days = bar.days || [];
  const weekDays = (week) => (week ? days.filter((d) => d.date >= week.start && d.date < addDays(week.start, 7)) : []);
  const [previous, last] = (bar.weeks || []).slice(-2).length === 2 ? bar.weeks.slice(-2) : [null, bar.weeks?.at(-1)];
  return {
    period: { key: "period", days, bar, durations: bar.timing?.durations || {} },
    week: last ? { key: "week", start: last.start, ...scopeOf(weekDays(last)) } : null,
    previous: previous ? scopeOf(weekDays(previous)) : null,
  };
}

// Mesures du bandeau, dans l'ordre de la barre « Respect du plan ». better : 1 si plus haut est mieux, −1 si plus bas ; gap : un écart
// au prévu, meilleur quand il se rapproche de zéro, qu'il soit en avance ou en retard.
export const METRICS = [
  { key: "stops", label: "Arrêts faits", unit: "pts", better: 1, threshold: 85, format: percent,
    value: (b) => pct(b.loads.done + b.deliveries.done, b.loads.planned + b.deliveries.planned) },
  { key: "order", label: "Ordre respecté", unit: "pts", better: 1, format: percent, value: (b) => pct(b.order.ok, b.order.count) },
  ...[["route", "Route par trajet"], ["loading", "Sur place · chargement"], ["delivery", "Sur place · livraison"]].map(([key, label]) => (
    { key, label, unit: "min", better: -1, gap: true, stage: key, format: fmtDelta, value: (b, d) => stageGap(d, key) })),
  { key: "off", label: "Hors plan", unit: "min", better: -1, threshold: 60, format: fmtDuration,
    value: (b) => (b.truck_days ? b.off_minutes / b.truck_days : null) },
  { key: "hours", label: "Heures travaillées", unit: "min", better: -1, gap: true, threshold: 30, format: fmtDelta,
    value: (b) => (b.hours.days ? (b.hours.real - b.hours.planned) / b.hours.days : null) },
];

export const metricOf = (key) => METRICS.find((m) => m.key === key) || METRICS[0];
// Sans camion-jour (un jour de semaine sans dispatch), pas de valeur.
export const valueOf = (metric, scope) => (scope?.bar?.truck_days ? metric.value(scope.bar, scope.durations) : null);
export const dayValue = (metric, day) => metric.value(day, day.durations);

// Écart avec la semaine précédente : en points pour un pourcentage, en minutes pour une durée.
export function weekDelta(metric, now, before) {
  const a = now && valueOf(metric, now);
  const b = before && valueOf(metric, before);
  if (a == null || b == null) return null;
  if (metric.unit === "pts") return resolveMetricDelta(a, b, { unit: "pts", higherIsBetter: metric.better > 0 });
  const diff = Math.round(a) - Math.round(b);
  if (!diff) return { value: 0, label: "= stable", tone: "flat" };
  const worse = metric.gap ? Math.abs(Math.round(a)) - Math.abs(Math.round(b)) : diff * -metric.better;
  return { value: diff, label: `${diff > 0 ? "▲ +" : "▼ −"}${fmtDuration(diff)}`, tone: worse > 0 ? "bad" : worse < 0 ? "good" : "flat" };
}

// Lundi à vendredi : la portée de chaque jour de la semaine sur la période.
const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"];
export function weekdayScopes(days) {
  return WEEKDAYS.map((name, i) => ({ name, ...scopeOf(days.filter((d) => new Date(`${d.date}T12:00:00Z`).getUTCDay() === i + 1)) }));
}

// Semaines de la période, leurs durées comprises.
export function weekScopes(bar) {
  const days = bar.days || [];
  return (bar.weeks || []).map((w) => ({ start: w.start, ...scopeOf(days.filter((d) => d.date >= w.start && d.date < addDays(w.start, 7))) }));
}
